import { type FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Select from '__components/Inputs/Select';
import Cols from '__components/Layout/Cols';
import { StyledModelMessage, StyledNotice } from './styles';
import type { LLMErrors, ModelOption, ProviderOption } from './types';

import { useFlashTransition } from '__hooks/useFlashState';

type LLMProviderSelectProps = {
  value: ModelOption | undefined;
  options: ProviderOption[];
  onChange: (model: ModelOption) => void;
  errors: LLMErrors;
  disabled: boolean;
  modelJustification?: string;
  addAudioMessage?: boolean;
};

export const LLMProviderSelect: FC<LLMProviderSelectProps> = ({
  value,
  options,
  onChange,
  errors,
  disabled,
  modelJustification,
  addAudioMessage = false,
}) => {
  const { t } = useTranslation();

  const [message, showMessage, flashMessage] = useFlashTransition();

  useEffect(() => {
    if (errors.provider?.hasError) {
      flashMessage(errors.provider.message);
    }
  }, [flashMessage, errors.provider]);

  const validate = useMemo(
    () => ({ message, showMessage }),
    [message, showMessage]
  );

  const notice = useMemo(() => {
    if (value?.model.is_deprecated) {
      const model = options
        .find((opt) => opt.details.id === value.model.business_plugin_app_id)
        ?.options.find((m) => m.value === value.model.suggested_replacement);
      return t(
        'This model is marked as deprecated due to the provider ending support. We recommend using {{recommendedModel}}.',
        {
          recommendedModel:
            model?.label ||
            value.model.suggested_replacement ||
            t('a different model'),
        }
      );
    } else {
      return modelJustification;
    }
  }, [modelJustification, value, options, t]);

  return (
    <>
      <Cols columns={3} gutter="20px">
        <Select
          label={t('Choose Model')}
          labelInfo={t(
            'Model choices are determined by both connections set up in your Marketplace as well as file capabilities supported by the model.'
          )}
          labelInfoPlacement="top"
          labelInfoMaxWidth={300}
          placeholder={t('Choose Model')}
          value={value}
          options={options}
          onChange={onChange}
          menuInline
          validate={validate}
          error={errors.provider?.hasError}
          disabled={disabled}
          captureMenuScroll={false}
        />
        {notice ? <StyledNotice>{notice}</StyledNotice> : null}
      </Cols>
      <Cols columns={1} gutter="20px">
        <StyledModelMessage
          fontStyle="italic"
          color="var(--color-font-placeholder)"
        >
          {addAudioMessage
            ? t(
                `Enable more providers to have access to more models. Note that each LLM model imposes its own limits on maximum audio recording duration and file size that it can transcribe.`
              )
            : t(`Enable more providers to have access to more models.`)}
        </StyledModelMessage>
      </Cols>
    </>
  );
};
